import React, { useState, useCallback } from "react";
import { Form, Button } from "react-bootstrap";
import { createSimpleStory, getBacklogStories } from "../../pages/api/backlog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const PlanningSprint = ({ onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskData, setTaskData] = useState({
    title: "",
    objective: "",
    points: "",
  });

  const [, setLoading] = useState(false);
  const [, setStories] = useState([]);
  const [titleError, setTitleError] = useState("");
  const [objectiveError, setObjectiveError] = useState("");
  const [pointsError, setPointsError] = useState("");
  const token = localStorage.getItem("token");

  const fetchBacklogStories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBacklogStories(token);
      console.log("backlogstories", res.data);
      setStories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch backlog stories", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleStoryChange = (e) => {
    const { name, value } = e.target;
    setTaskData((prev) => ({ ...prev, [name]: value }));

    if (name === "title") {
      const trimmedValue = value.trim();

      if (value === "") {
        setTitleError("");
        return;
      }

      if (trimmedValue === "") {
        setTitleError("Story/Task Title cannot be spaces only");
        return;
      }

      if (/^[0-9]+$/.test(trimmedValue)) {
        setTitleError("Story/Task Title cannot be numbers only");
        return;
      }

      if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
        setTitleError(
          "Story/Task Title cannot contain only special characters"
        );
        return;
      }

      if (!/[a-zA-Z]/.test(trimmedValue)) {
        setTitleError("Story/Task Title must contain at least one alphabet");
        return;
      }

      setTitleError("");
    }
  };

  const handleObjectiveChange = (e) => {
    const { name, value } = e.target;
    setTaskData((prev) => ({ ...prev, [name]: value }));

    if (name === "objective") {
      const trimmedValue = value.trim();
      if (value === "") {
        setObjectiveError("");
        return;
      } else if (trimmedValue === "") {
        setObjectiveError("Story Description cannot be spaces only");
        return;
      } else if (/^[0-9]+$/.test(trimmedValue)) {
        setObjectiveError("Story Description cannot be numbers only");
        return;
      } else if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
        setObjectiveError(
          "Story Description cannot contain only special characters"
        );
        return;
      } else if (!/[a-zA-Z]/.test(trimmedValue)) {
        setObjectiveError(
          "Story Description must contain at least one alphabet"
        );
        return;
      }
      setObjectiveError("");
    }
  };

  const handlePointsChange = (e) => {
    const { name, value } = e.target;

    setTaskData((prev) => ({ ...prev, [name]: value }));

    const trimmedValue = value.trim();

    if (trimmedValue === "") {
      if (value !== "") {
        setPointsError("Story Points cannot contain spaces only");
      } else {
        setPointsError("");
      }
    } else if (!/^[0-9]+$/.test(trimmedValue)) {
      setPointsError("Story Points must be numeric");
    } else {
      setPointsError("");
    }
  };

  const handleSubmit = async () => {
    const { title, objective, points } = taskData;
    setTitleError("");
    setObjectiveError("");
    setPointsError("");
    let hasError = false;
    if (title === "") {
      setTitleError("Story/Task Title is required");
      hasError = true;
    } else if (title.trim() === "") {
      setTitleError("Story/Task Title cannot be spaces only");
      hasError = true;
    } else if (/^[0-9]+$/.test(title.trim())) {
      setTitleError("Story/Task Title cannot be numbers only");
      hasError = true;
    } else if (/^[^a-zA-Z0-9]+$/.test(title.trim())) {
      setTitleError("Story/Task Title cannot contain only special characters");
      hasError = true;
    } else if (!/[a-zA-Z]/.test(title.trim())) {
      setTitleError("Story/Task Title must contain at least one alphabet");
      hasError = true;
    }
    if (objective === "") {
      setObjectiveError("Story Description is required");
      hasError = true;
    } else if (objective.trim() === "") {
      setObjectiveError("Story Description cannot be spaces only");
      hasError = true;
    } else if (/^[0-9]+$/.test(objective.trim())) {
      setObjectiveError("Story Description cannot be numbers only");
      hasError = true;
    } else if (/^[^a-zA-Z0-9]+$/.test(objective.trim())) {
      setObjectiveError(
        "Story Description cannot contain only special characters"
      );
      hasError = true;
    } else if (!/[a-zA-Z]/.test(objective.trim())) {
      setObjectiveError("Story Description must contain at least one alphabet");
      hasError = true;
    }
    if (points && points.trim() !== "" && !/^[0-9]+$/.test(points.trim())) {
      setPointsError("Story Points must be numeric");
      hasError = true;
    } else if (points && points.trim() === "") {
      setPointsError("Story Points cannot contain spaces only");
      hasError = true;
    }
    if (hasError) return;
    setIsSubmitting(true);
    try {
      const storyRequest = {
        storyTitle: title.trim(),
        storyDescription: objective.trim(),
        storyPoints: points ? parseInt(points, 10) : 0,
      };

      const response = await createSimpleStory(storyRequest, token);
      console.log("backlogstories", response.data);
      if (response?.status >= 200 && response?.status < 300) {
        const createdStory = response.data;

        toast.success("Story created successfully!");
        await fetchBacklogStories();
        if (typeof onSave === "function") {
          onSave(createdStory); 
        }
        if (typeof onClose === "function") {
          onClose();
        }
      } else {
        toast.error("Failed to create story");
      }
    } catch (error) {
      console.error("Error creating story:", error);
      toast.error("An error occurred while creating the story");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-2">
      <Form>
        <Form.Group className="mb-3">
          <Form.Label>
            Story Title<span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Story Title"
            value={taskData.title}
            onChange={handleStoryChange}
            name="title"
          />
          {titleError && (
            <div
              style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
            >
              {titleError}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3 position-relative">
          <Form.Label>
            Story Description<span className="text-danger">*</span>
          </Form.Label>

          <Form.Control
            as="textarea"
            name="objective"
            rows={4}
            placeholder="Enter Story Description"
            value={taskData.objective}
            onChange={handleObjectiveChange}
            style={{ paddingBottom: "40px" }}
          />

       

          {objectiveError && (
            <div
              style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
            >
              {objectiveError}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3 position-relative">
          <Form.Label>Story Points</Form.Label>
          <Form.Control
            type="text"
            name="points"
            placeholder="Story Points"
            value={taskData.points}
            onChange={handlePointsChange}
          />
          {pointsError && (
            <div
              style={{ color: "red", fontSize: "0.75rem", marginTop: "4px" }}
            >
              {pointsError}
            </div>
          )}
        </Form.Group>
      </Form>

      <div className="d-flex justify-content-end mt-3 gap-2">
        <Button
          className="taskButton"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Task"}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>

      <ToastContainer />
    </div>
  );
};

export default PlanningSprint;
